import type { GeometryAnalyzeCandidate, GeometryAnalyzeResponse } from "@/types/api-v2";

export type RdIntersectionReportInput = {
  requestId: number;
  requestTypeName?: string | null;
  requestSource?: string | null;
  requestDocNum?: string | null;
  requestDocDate?: string | null;
  requestMaker?: string | null;
  geometryName: string;
  requestMapGeometryId: number;
  geometrySourceLabel: string;
  /** Буферная зона анализа в метрах (как ушло в API). */
  toleranceMeters: number;
  analysis: GeometryAnalyzeResponse;
  generatedAt?: Date;
};

function formatBufferKm(meters: number): string {
  if (!Number.isFinite(meters)) return "—";
  const km = meters / 1000;
  const text = Number.isInteger(km) ? String(km) : km.toFixed(1).replace(/\.0$/, "");
  return `${text} км`;
}

const RELATION_LABELS: Record<string, string> = {
  inside: "Внутри контура",
  intersects: "Пересекает",
  near: "Рядом (буфер)",
};

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatDateRu(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return escapeHtml(value);
  return d.toLocaleDateString("ru-RU");
}

function formatDateTimeRu(d: Date): string {
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAreaSqm(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} км²`;
  if (v >= 10_000) return `${(v / 10_000).toFixed(2)} га`;
  return `${v.toFixed(1)} м²`;
}

function formatRatio(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)} %`;
}

function relationLabel(relation: string): string {
  return RELATION_LABELS[relation] ?? relation;
}

function candidateRows(candidates: GeometryAnalyzeCandidate[]): string {
  if (candidates.length === 0) {
    return `<tr><td colspan="7" class="empty">Пересечений не найдено</td></tr>`;
  }
  return candidates
    .map((c, index) => {
      return `<tr>
        <td class="num">${index + 1}</td>
        <td>${escapeHtml(c.object_name)}</td>
        <td class="mono">${c.object_id}</td>
        <td class="mono">${c.geometry_id}</td>
        <td><span class="badge badge-${escapeHtml(c.relation)}">${escapeHtml(relationLabel(c.relation))}</span></td>
        <td class="num">${escapeHtml(formatAreaSqm(c.intersection_area_sqm))}</td>
        <td class="num">${escapeHtml(formatRatio(c.intersection_ratio))}</td>
      </tr>`;
    })
    .join("\n");
}

function buildReportHtml(input: RdIntersectionReportInput): string {
  const generatedAt = input.generatedAt ?? new Date();
  const { analysis } = input;
  const count = analysis.candidates.length;

  return `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="utf-8" />
  <title>Отчёт о пересечениях — заявка №${escapeHtml(input.requestDocNum || input.requestId)}</title>
  <style>
    :root {
      --ink: #1a2332;
      --muted: #5c6b7a;
      --line: #d8dee6;
      --brand: #0f4c81;
      --brand-soft: #e8f1f8;
      --inside: #0d7a4f;
      --intersects: #b45309;
      --near: #475569;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 28px 32px 40px;
      font-family: "Segoe UI", "Liberation Sans", Arial, sans-serif;
      color: var(--ink);
      font-size: 12.5px;
      line-height: 1.45;
      background: #fff;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      border-bottom: 3px solid var(--brand);
      padding-bottom: 16px;
      margin-bottom: 22px;
    }
    .brand {
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--brand);
      font-weight: 700;
      margin-bottom: 6px;
    }
    h1 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      color: var(--ink);
    }
    .subtitle {
      margin-top: 4px;
      color: var(--muted);
      font-size: 12px;
    }
    .meta-stamp {
      text-align: right;
      color: var(--muted);
      font-size: 11px;
      white-space: nowrap;
    }
    .meta-stamp strong { color: var(--ink); display: block; font-size: 12px; margin-bottom: 2px; }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px 20px;
      margin-bottom: 20px;
    }
    .card {
      border: 1px solid var(--line);
      border-radius: 8px;
      padding: 12px 14px;
      background: #fafbfc;
    }
    .card h2 {
      margin: 0 0 10px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--brand);
    }
    .kv { display: grid; grid-template-columns: 140px 1fr; gap: 6px 10px; }
    .kv dt { color: var(--muted); }
    .kv dd { margin: 0; font-weight: 600; }
    .summary {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin: 0 0 18px;
    }
    .pill {
      border-radius: 999px;
      padding: 6px 12px;
      background: var(--brand-soft);
      color: var(--brand);
      font-weight: 600;
      font-size: 12px;
    }
    .pill.warn { background: #fff7ed; color: #9a3412; }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
    }
    th, td {
      border: 1px solid var(--line);
      padding: 8px 9px;
      vertical-align: top;
      text-align: left;
    }
    th {
      background: var(--brand);
      color: #fff;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    tr:nth-child(even) td { background: #f7f9fb; }
    td.num, th.num { text-align: right; white-space: nowrap; }
    td.mono { font-family: ui-monospace, "Cascadia Mono", Consolas, monospace; font-size: 11px; }
    td.empty { text-align: center; color: var(--muted); padding: 18px; }
    .badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 600;
      white-space: nowrap;
    }
    .badge-inside { background: #dcfce7; color: var(--inside); }
    .badge-intersects { background: #ffedd5; color: var(--intersects); }
    .badge-near { background: #e2e8f0; color: var(--near); }
    .section-title {
      margin: 8px 0 6px;
      font-size: 14px;
      font-weight: 700;
    }
    .footnote {
      margin-top: 22px;
      padding-top: 12px;
      border-top: 1px solid var(--line);
      color: var(--muted);
      font-size: 10.5px;
    }
    .print-hint {
      margin-bottom: 14px;
      padding: 10px 12px;
      background: #fffbeb;
      border: 1px solid #fcd34d;
      border-radius: 6px;
      color: #92400e;
      font-size: 12px;
    }
    @media print {
      body { padding: 12mm 10mm; }
      .print-hint { display: none !important; }
      .card { break-inside: avoid; }
      thead { display: table-header-group; }
      tr { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="print-hint">
    Для сохранения в PDF выберите в диалоге печати принтер «Сохранить как PDF» / «Microsoft Print to PDF», затем «Печать».
  </div>
  <header class="header">
    <div>
      <div class="brand">GeoPulseML · Модуль РД</div>
      <h1>Отчёт о пересечениях контура заявки</h1>
      <div class="subtitle">Результат пространственного анализа геометрии территории запроса</div>
    </div>
    <div class="meta-stamp">
      <strong>Сформировано</strong>
      ${escapeHtml(formatDateTimeRu(generatedAt))}
    </div>
  </header>

  <div class="grid">
    <section class="card">
      <h2>Заявка</h2>
      <dl class="kv">
        <dt>ID заявки</dt><dd>${input.requestId}</dd>
        <dt>Тип</dt><dd>${escapeHtml(input.requestTypeName || "—")}</dd>
        <dt>Источник</dt><dd>${escapeHtml(input.requestSource || "—")}</dd>
        <dt>№ документа</dt><dd>${escapeHtml(input.requestDocNum || "—")}</dd>
        <dt>Дата документа</dt><dd>${formatDateRu(input.requestDocDate)}</dd>
        <dt>Заявитель</dt><dd>${escapeHtml(input.requestMaker || "—")}</dd>
      </dl>
    </section>
    <section class="card">
      <h2>Геометрия анализа</h2>
      <dl class="kv">
        <dt>Название</dt><dd>${escapeHtml(input.geometryName)}</dd>
        <dt>ID записи РД</dt><dd>${input.requestMapGeometryId}</dd>
        <dt>Источник контура</dt><dd>${escapeHtml(input.geometrySourceLabel)}</dd>
        <dt>Буферная зона</dt><dd>${escapeHtml(formatBufferKm(input.toleranceMeters))}</dd>
        <dt>Площадь контура</dt><dd>${escapeHtml(formatAreaSqm(analysis.drawn_area_sqm))}</dd>
        <dt>Порог доли</dt><dd>${escapeHtml(formatRatio(analysis.ratio_threshold_used))}</dd>
        <dt>Несколько орг.</dt><dd>${analysis.multiple_organizations ? "Да" : "Нет"}</dd>
      </dl>
    </section>
  </div>

  <div class="summary">
    <span class="pill">Буферная зона: ${escapeHtml(formatBufferKm(input.toleranceMeters))}</span>
    <span class="pill">Найдено пересечений: ${count}</span>
    ${
      analysis.suggested_primary_object_id != null
        ? `<span class="pill">Рекомендуемый объект ОИ: ${analysis.suggested_primary_object_id}</span>`
        : ""
    }
    ${
      analysis.multiple_organizations
        ? `<span class="pill warn">Кандидаты из разных организаций</span>`
        : ""
    }
  </div>

  <div class="section-title">Список найденных объектов и геометрий</div>
  <table>
    <thead>
      <tr>
        <th class="num">№</th>
        <th>Объект ОИ</th>
        <th>ID объекта</th>
        <th>ID геометрии</th>
        <th>Тип отношения</th>
        <th class="num">Площадь пересечения</th>
        <th class="num">Доля пересечения</th>
      </tr>
    </thead>
    <tbody>
      ${candidateRows(analysis.candidates)}
    </tbody>
  </table>

  <footer class="footnote">
    Отчёт сформирован в рабочем контуре модуля РД (согласования). Типы отношений:
    «Внутри контура» — объект полностью или преимущественно внутри анализируемого контура;
    «Пересекает» — частичное пересечение границ;
    «Рядом» — объект в пределах допускового буфера.
    Документ предназначен для служебного использования при согласовании.
  </footer>
</body>
</html>`;
}

/**
 * Печать через скрытый iframe (без popup): диалог печати / «Сохранить как PDF».
 * Не использовать window.open(..., "noopener") — браузер вернёт null при открытом окне.
 */
export function printRdIntersectionReport(input: RdIntersectionReportInput): void {
  const html = buildReportHtml(input);
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Печать отчёта о пересечениях");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDoc = frameWindow?.document ?? iframe.contentDocument;
  if (!frameWindow || !frameDoc) {
    iframe.remove();
    throw new Error("Не удалось подготовить документ для печати.");
  }

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  const cleanup = () => {
    iframe.remove();
  };

  const trigger = () => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } finally {
      // После диалога печати iframe можно убрать с небольшой задержкой.
      window.setTimeout(cleanup, 1000);
    }
  };

  if (frameDoc.readyState === "complete") {
    window.setTimeout(trigger, 100);
  } else {
    iframe.addEventListener("load", () => window.setTimeout(trigger, 100), { once: true });
  }
}
